import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function XeroConfig() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    redirect_uri: `${API_URL}/xero/callback`,
    tenant_id: '',
    scopes: [
      'accounting.transactions',
      'accounting.contacts.read',
      'accounting.settings',
    ],
  });

  useEffect(() => {
    fetchCurrentConfig();
  }, []);

  const fetchCurrentConfig = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/system-settings/xero/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch Xero configuration');

      const data = await response.json();
      if (data.configured && data.config) {
        setConfig(data.config);
        setFormData({
          client_id: data.config.client_id || '',
          client_secret: data.config.client_secret ? '••••••••' : '',
          redirect_uri: data.config.redirect_uri || formData.redirect_uri,
          tenant_id: data.config.tenant_id || '',
          scopes: data.config.scopes || formData.scopes,
        });
        setStep(3); // Go to review step if already configured
      }
    } catch (err) {
      console.error('Failed to load Xero configuration:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleScopeToggle = (scope) => {
    const scopes = formData.scopes.includes(scope)
      ? formData.scopes.filter((s) => s !== scope)
      : [...formData.scopes, scope];
    setFormData({ ...formData, scopes });
  };

  const validateStep1 = () => {
    if (!formData.client_id || !formData.client_secret || !formData.redirect_uri) {
      setError('All fields are required');
      return false;
    }
    if (!formData.redirect_uri.startsWith('http://') && !formData.redirect_uri.startsWith('https://')) {
      setError('Redirect URI must be a valid URL');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    setError('');
    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/system-settings/xero/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Connection test failed');
      }

      setSuccess('Connection test successful! Your Xero configuration is valid.');
    } catch (err) {
      setError(err.message || 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/system-settings/xero/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          client_secret: formData.client_secret === '••••••••' ? undefined : formData.client_secret,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      setSuccess('Xero configuration saved successfully!');
      await fetchCurrentConfig();
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const availableScopes = [
    { value: 'accounting.transactions', label: 'Transactions', description: 'Read and write transactions' },
    { value: 'accounting.contacts.read', label: 'Read Contacts', description: 'Read contact information' },
    { value: 'accounting.contacts', label: 'Contacts', description: 'Read and write contacts' },
    { value: 'accounting.settings', label: 'Settings', description: 'Read accounting settings' },
    { value: 'accounting.reports.read', label: 'Reports', description: 'Read financial reports' },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Xero Integration Setup</h1>
        <p className="text-gray-600 mt-1">Configure your Xero accounting software connection</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {[1, 2, 3].map((stepNum) => (
            <div key={stepNum} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                    step >= stepNum
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step > stepNum ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    stepNum
                  )}
                </div>
                <div className="mt-2 text-sm font-medium text-gray-700">
                  {stepNum === 1 && 'Credentials'}
                  {stepNum === 2 && 'Scopes'}
                  {stepNum === 3 && 'Review'}
                </div>
              </div>
              {stepNum < 3 && (
                <div
                  className={`flex-1 h-1 mx-4 ${
                    step > stepNum ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Wizard Content */}
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-3xl mx-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            <span>{success}</span>
          </div>
        )}

        {/* Step 1: Credentials */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Step 1: Xero API Credentials</h2>
              <p className="text-gray-600 mb-6">
                Enter your Xero application credentials. If you don't have them yet,{' '}
                <a
                  href="https://developer.xero.com/myapps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                >
                  create a new app in Xero Developer Portal
                  <ExternalLink className="w-4 h-4" />
                </a>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client ID *
                </label>
                <input
                  type="text"
                  required
                  value={formData.client_id}
                  onChange={(e) => handleInputChange('client_id', e.target.value)}
                  placeholder="Enter your Xero Client ID"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Found in your Xero app settings
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Secret *
                </label>
                <input
                  type="password"
                  required
                  value={formData.client_secret}
                  onChange={(e) => handleInputChange('client_secret', e.target.value)}
                  placeholder="Enter your Xero Client Secret"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Keep this secure and never share it
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Redirect URI *
                </label>
                <input
                  type="url"
                  required
                  value={formData.redirect_uri}
                  onChange={(e) => handleInputChange('redirect_uri', e.target.value)}
                  placeholder="https://your-domain.com/api/xero/callback"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must match the redirect URI configured in your Xero app
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.tenant_id}
                  onChange={(e) => handleInputChange('tenant_id', e.target.value)}
                  placeholder="Enter your Xero Tenant ID (if known)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usually obtained during OAuth flow
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Scopes */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Step 2: Select Permissions</h2>
              <p className="text-gray-600 mb-6">
                Choose which Xero API permissions your application needs
              </p>
            </div>

            <div className="space-y-3">
              {availableScopes.map((scope) => (
                <label
                  key={scope.value}
                  className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.scopes.includes(scope.value)}
                    onChange={() => handleScopeToggle(scope.value)}
                    className="mt-1 mr-3 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{scope.label}</div>
                    <div className="text-sm text-gray-500">{scope.description}</div>
                    <div className="text-xs text-gray-400 font-mono mt-1">{scope.value}</div>
                  </div>
                </label>
              ))}
            </div>

            {formData.scopes.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
                Please select at least one permission scope
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Step 3: Review & Save</h2>
              <p className="text-gray-600 mb-6">
                Review your configuration and save it to complete the setup
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Client ID</div>
                <div className="text-lg font-mono text-gray-900">{formData.client_id || '-'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Client Secret</div>
                <div className="text-lg font-mono text-gray-900">
                  {formData.client_secret === '••••••••' ? '••••••••' : '••••••••'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Redirect URI</div>
                <div className="text-lg text-gray-900">{formData.redirect_uri}</div>
              </div>
              {formData.tenant_id && (
                <div>
                  <div className="text-sm font-medium text-gray-500">Tenant ID</div>
                  <div className="text-lg font-mono text-gray-900">{formData.tenant_id}</div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-500 mb-2">Selected Scopes</div>
                <div className="flex flex-wrap gap-2">
                  {formData.scopes.map((scope) => (
                    <span
                      key={scope}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-mono"
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {config && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
                <div className="font-medium">Configuration Status</div>
                <div className="text-sm mt-1">
                  Last configured: {config.configured_at
                    ? new Date(config.configured_at).toLocaleString()
                    : 'Unknown'}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          {step < 3 && (
            <button
              onClick={handleNext}
              disabled={step === 2 && formData.scopes.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

